import { LOCALE_ID, ErrorHandler, NgModule, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MARKED_OPTIONS, MarkedOptions, MarkedRenderer, provideMarkdown } from 'ngx-markdown';
import { Tokens } from 'marked';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { SharedBaseModule } from './shared/shared-base.module';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MaterialModule } from '@shared/material.module';
import * as Sentry from "@sentry/angular";

import { registerLocaleData } from '@angular/common';
import localeNl from '@angular/common/locales/nl';
import localeFr from '@angular/common/locales/fr';
import { DateInputConverter } from '@shared/directives/date-input-converter';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';
import { LineBreakPipe } from '@shared/pipes/line-break-pipe';
import { UnauthInterceptor } from '@core/interceptors/unauth.interceptor';
import { SamlService } from '@modules/saml/services/saml.service';
import { NavigationService } from '@core/services/navigation.service';
import { BranchService } from '@core/services/branch.service';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { Router } from '@angular/router';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { nlBE } from 'date-fns/locale';



export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'dd/MM/yyyy',
  },
  display: {
    dateInput: 'dd/MM/yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'PP',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

registerLocaleData(localeNl);
registerLocaleData(localeFr);


export function markedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();
  const defaultLink = renderer.link?.bind(renderer);

  // Use a normal function so `this` is the active renderer with a parser.
  renderer.link = function (token: Tokens.Link) {
    const { href, title, tokens } = token;

    if (href?.startsWith('course:')) {
      const courseId = href.slice('course:'.length);
      const t = title ? ` title="${title}"` : '';
      return `<a href="#" data-course-id="${courseId}"${t}>${this.parser.parseInline(tokens)}</a>`;
    }

    // fallback to default marked behavior
    return defaultLink
      ? defaultLink(token)
      : `<a href="${href ?? ''}" target="_blank">${this.parser.parseInline(tokens)}</a>`;
  };

  return {
    renderer,
    gfm: true,
    breaks: false,
  };
}


@NgModule({ declarations: [
        AppComponent,
        DateInputConverter
    ],
    bootstrap: [AppComponent], 
    imports: [
        BrowserModule,
        FormsModule,
        ReactiveFormsModule,
        AppRoutingModule,
        // Only what the shell draws: the feature screens load with the lazy modules (see SharedBaseModule).
        SharedBaseModule,
        BrowserAnimationsModule,
        MaterialModule
    ],
    providers: [
        // Scrolling a list fires scroll events far faster than a frame, and each one used to run its own
        // change detection pass over the whole page — which is what makes a long list render in pieces
        // while it is scrolled. Coalescing runs one pass per frame instead.
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideMarkdown({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
        }),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json',
                enforceLoading: true
            }),
            fallbackLang: 'nl',
            lang: 'nl'
        }),
        // Settle branch/vanity detection before the app bootstraps, so guards/components never read a
        // not-yet-resolved vanityMode (which previously defaulted to false → vanity hosts mis-detected).
        provideAppInitializer(() => inject(BranchService).autoDetectBranch()),
        NavigationService,
        SamlService,
        LineBreakPipe,
        provideCharts(withDefaultRegisterables()),
        { provide: DateAdapter, useClass: DateFnsAdapter },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: nlBE },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UnauthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: LOCALE_ID, useValue: 'nl' },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
    ] })
export class AppModule { }